import type { PointerPosition } from './experience'

export type PointerKind = 'mouse' | 'pen' | 'touch'

export interface PointerBounds {
  left: number
  top: number
  width: number
  height: number
}

export interface LocalPointer extends PointerPosition {
  percentX: number
  percentY: number
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value))

export function getEffectivePointerKind(
  reportedKind: PointerKind,
  hasFinePointer: boolean,
): PointerKind {
  return reportedKind === 'mouse' && !hasFinePointer ? 'touch' : reportedKind
}

export function dampPointerValue(
  current: number,
  target: number,
  smoothing: number,
  deltaMs: number,
): number {
  const deltaSeconds = clamp(deltaMs, 0, 64) / 1000
  const amount = 1 - Math.exp(-Math.max(smoothing, 0) * deltaSeconds)
  return current + (target - current) * amount
}

export function getLocalPointer(
  clientX: number,
  clientY: number,
  bounds: PointerBounds,
): LocalPointer {
  const width = Math.max(bounds.width, 1)
  const height = Math.max(bounds.height, 1)
  const horizontal = clamp((clientX - bounds.left) / width, 0, 1)
  const vertical = clamp((clientY - bounds.top) / height, 0, 1)

  return {
    x: horizontal * 2 - 1,
    y: 1 - vertical * 2,
    percentX: horizontal * 100,
    percentY: vertical * 100,
  }
}

export function getPointerDepthStrength(kind: PointerKind, reducedMotion: boolean): number {
  if (reducedMotion) return 0
  return kind === 'touch' ? 0.34 : kind === 'pen' ? 0.72 : 1
}

export function shouldReturnPointerToRest(
  now: number,
  lastInputAt: number,
  kind: PointerKind,
): boolean {
  const inactivityWindow = kind === 'touch' ? 720 : 1_200
  return now - lastInputAt > inactivityWindow
}
